import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { cn } from '../../utils/cn';
import toast from 'react-hot-toast';

/**
 * QuickSettingsDrawer Component
 * Right-side drawer for creating/editing entities
 * 
 * @param {Boolean} open - Whether drawer is open
 * @param {Function} onClose - Callback when drawer is closed
 * @param {Object} entity - Entity being edited (null for create)
 * @param {String} entityType - Type of entity (tenant, organization, department, team, user)
 * @param {Function} onSave - Callback when entity is saved
 */
const QuickSettingsDrawer = ({ open, onClose, entity, entityType, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      initializeForm();
    }
  }, [open, entity, entityType]);

  const initializeForm = () => {
    if (entity) {
      // Edit mode - populate with entity data
      setFormData({
        name: entity.name || entity.data?.tenantName || entity.data?.organizationName || 
              entity.data?.departmentName || entity.data?.teamName || 
              entity.data?.fullName || entity.data?.username || '',
        code: entity.data?.tenantCode || entity.data?.organizationCode || 
              entity.data?.departmentCode || entity.data?.teamCode || '',
        description: entity.data?.description || '',
        status: entity.data?.status || entity.data?.isActive ? 'active' : 'inactive',
        email: entity.data?.email || '',
        // Add more fields as needed
      });
    } else {
      // Create mode - empty form
      setFormData({
        name: '',
        code: '',
        description: '',
        status: 'active',
        email: '',
      });
    }
    setErrors({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    if (entityType !== 'user' && (!formData.code || formData.code.trim() === '')) {
      newErrors.code = 'Code is required';
    }
    
    if (entityType === 'user' && (!formData.email || formData.email.trim() === '')) {
      newErrors.email = 'Email is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }
    
    setLoading(true);
    try {
      await onSave(formData, entity);
      toast.success(entity ? 'Entity updated successfully' : 'Entity created successfully');
      onClose();
    } catch (error) {
      console.error('Error saving entity:', error);
      toast.error(error.response?.data?.message || 'Failed to save entity');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (!entityType) return 'Entity Settings';
    const action = entity ? 'Edit' : 'Create';
    const type = entityType.charAt(0).toUpperCase() + entityType.slice(1);
    return `${action} ${type}`;
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div
        className={cn(
          "fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">
                {entityType === 'user' ? 'Full Name' : 'Name'} *
              </Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={`Enter ${entityType === 'user' ? 'full name' : 'name'}`}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Code (not for users) */}
            {entityType !== 'user' && (
              <div>
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code || ''}
                  onChange={(e) => handleChange('code', e.target.value)}
                  placeholder="Enter code"
                  className={errors.code ? 'border-red-500' : ''}
                />
                {errors.code && (
                  <p className="text-sm text-red-500 mt-1">{errors.code}</p>
                )}
              </div>
            )}

            {/* Email (for users) */}
            {entityType === 'user' && (
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter email"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Enter description"
                rows={3}
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                id="status"
                value={formData.status || 'active'}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                {entityType === 'tenant' && (
                  <>
                    <option value="suspended">Suspended</option>
                  </>
                )}
              </Select>
            </div>

            {/* Additional fields based on entity type */}
            {entityType === 'tenant' && (
              <>
                <div>
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain || ''}
                    onChange={(e) => handleChange('domain', e.target.value)}
                    placeholder="Enter domain"
                  />
                </div>
                <div>
                  <Label htmlFor="subscriptionTier">Subscription Tier</Label>
                  <Select
                    id="subscriptionTier"
                    value={formData.subscriptionTier || 'standard'}
                    onChange={(e) => handleChange('subscriptionTier', e.target.value)}
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </Select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default QuickSettingsDrawer;

